import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../app/store";
import { fetchEmpOracleHierarchyDetails } from "./GetHierarchyDetailsSlice";
import {
  fetchEmpDetails,
  resetEmpDetails,
} from "../CareerCard/CareerCardDetailsSlice";
import {
  fetchEmpOracleDetails,
  resetEmpOracleDetails,
} from "../CareerCard/EmpOracleDetailsSlice";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { createRoot } from "react-dom/client";
import CareerCardHtml from "../CareerCard/CareerCardPdfTemplate";
import { EmpDetails } from "../CareerCard/CareerCardDetailsSlice";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import CareerCard from "../CareerCard/CareerCard";
import "./CareerCardModal.css";
import { Button } from "primereact/button";
import "bootstrap-icons/font/bootstrap-icons.css";

const Toast = ({
  message,
  type,
  onClose,
}: {
  message: string;
  type: "success" | "error" | "warning";
  onClose: () => void;
}) => {
  const bgColor = {
    success: "#28a745",
    error: "#dc3545",
    warning: "#ffc107",
  }[type];

  const textColor = type === "warning" ? "#000" : "#fff";

  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        backgroundColor: bgColor,
        color: textColor,
        padding: "12px 20px",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        zIndex: 10000,
        maxWidth: "400px",
        fontSize: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        animation: "slideIn 0.3s ease-out",
      }}
    >
      <div style={{ flex: 1, marginRight: "10px" }}>{message}</div>
      <button
        onClick={onClose}
        style={{
          background: "transparent",
          border: "none",
          color: textColor,
          fontSize: "18px",
          cursor: "pointer",
          padding: "0",
          lineHeight: "1",
        }}
        aria-label="Close notification"
      >
        <i className="bi bi-x" aria-hidden="true"></i>
      </button>
      <style>
        {`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

// interface ReporteeDetailsProps {
//   onBack: () => void;
//   onViewDetails: (email: string) => void;
//   managerOracleId?: string;
// }

interface Reportee {
  name: string;
  oracleId: string;
  jobLevel: string;
  timeAtTTEC: string;
  imageUrl: string;
  workEmail: string;
}

interface EmployeeData {
  employee_id: string;
  employee_name: string;
  job_level: string;
  manager_id: string;
  manager_name: string;
  hire_date: string;
  work_email: string;
}

interface ToastNotification {
  id: number;
  message: string;
  type: "success" | "error" | "warning";
}

const DEFAULT_IMAGE = "/profile.png";

const ReporteeDetails = (props: any) => {
  const dispatch = useDispatch<AppDispatch>();
  const hierarchyDetails = useSelector(
    (state: RootState) => state.getHierarchyDetails,
  );
  const processedCountRef = useRef(0);
  const apiFailureCountRef = useRef(0);
  const [reportees, setReportees] = useState<Reportee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReportees, setSelectedReportees] = useState<Set<string>>(
    new Set(),
  );
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [showExportValidation, setShowExportValidation] =
    useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportProgress, setExportProgress] = useState<{
    current: number;
    total: number;
    phase: string;
  }>({
    current: 0,
    total: 0,
    phase: "idle",
  });

  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  const [nextToastId, setNextToastId] = useState(1);
  const [showProgressIndicator, setShowProgressIndicator] =
    useState<boolean>(true);

  // Add initial loading progress state
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [initialLoadingProgress, setInitialLoadingProgress] = useState({
    current: 0,
    total: 1,
    status: "Loading team data...",
  });

  const currentManagerOracleId = "7515356";

  // Modal state for Career Card
  const [showCareerCardModal, setShowCareerCardModal] =
    useState<boolean>(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const showToast = (
    message: string,
    type: "success" | "error" | "warning",
  ) => {
    const newToast: ToastNotification = {
      id: nextToastId,
      message,
      type,
    };
    setToasts((prev) => [...prev, newToast]);
    setNextToastId((prev) => prev + 1);

    setTimeout(() => {
      removeToast(newToast.id);
    }, 5000);
  };

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  // Initial Loading Progress Indicator
  const InitialLoadingProgress = () => (
    <div
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        backgroundColor: "rgba(255, 255, 255, 0.98)",
        color: "#333333",
        padding: "30px 40px",
        borderRadius: "12px",
        border: "2px solid rgb(244, 247, 249)",
        boxShadow: "0 15px 35px rgba(0, 0, 0, 0.3)",
        zIndex: 9999,
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "500",
        minWidth: "300px",
      }}
    >
      <div className="mb-3">
        <div
          className="spinner-border text-primary mb-2"
          style={{ width: "2.5rem", height: "2.5rem" }}
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="fw-bold">Loading Team Data</div>
      </div>

      <div className="mb-2">
        <div className="progress" style={{ height: "8px" }}>
          <div
            className="progress-bar bg-primary"
            style={{
              width: `${(initialLoadingProgress.current / initialLoadingProgress.total) * 100}%`,
              transition: "width 0.5s ease",
            }}
          ></div>
        </div>
      </div>

      <div className="small text-muted">{initialLoadingProgress.status}</div>

      <div className="mt-2 small">
        {initialLoadingProgress.current} of {initialLoadingProgress.total}{" "}
        completed
      </div>
    </div>
  );

  // Monitor initial loading progress
  useEffect(() => {
    const hierarchyStatus = hierarchyDetails.status;

    let completed = 0;
    let status = "Loading team hierarchy...";

    if (hierarchyStatus === "succeeded" || hierarchyStatus === "failed") {
      completed = 1;
      status =
        hierarchyStatus === "succeeded"
          ? "Team data loaded successfully!"
          : "Loading completed";
    }

    setInitialLoadingProgress({ current: completed, total: 1, status });

    // Hide loading when complete
    if (completed === 1) {
      setTimeout(() => {
        setIsInitialLoading(false);
        setLoading(false);
      }, 500);
    }
  }, [hierarchyDetails.status]);

  useEffect(() => {
    return () => {
      dispatch(resetEmpDetails());
      dispatch(resetEmpOracleDetails());
    };
  }, [dispatch]);

  const processEmployeeData = (
    empDetails: any,
    empOracleDetails: any,
    basicReporteeData: Reportee,
  ) => {
    const processedData = {
      ...basicReporteeData,
      name: basicReporteeData?.name || "",
      jobLevel: "",
      jobTitle: "",
      timeAtTTEC: "",
      time_in_current_role: "",
      education_degree: "",
      certificates: "",
      location_desc: "",
      willingness_to_relocate: "",
      next_role_preference: "",
      timing_for_next_role: "",
      last_year_top_achievement: "",
      last_year_personal_development_review: "",
      goal_next_two_quarters: "",
      image: basicReporteeData?.imageUrl || "",
      emp_languages: [],
      EmpLocationPreferences: [],
      work_experiences: [],
      EmpResourceGroup: [],
      EmpTtecCommunities: [],
      EmpLeadershipCulture: [],
      oracle_id: basicReporteeData?.oracleId || "",
      manager_oracle: "",
      manager_email: "",
      country: "",
      employee_status: "",
      accomplishments: "",
      redo: "",
      goals: "",
      resourceGroup: "",
      ttecCommunity: "",
      strength: "",
      development: "",
      ttecRoles: [],
      languages: [],
      locationPreferences: [],
      strengthsDisplayed: [],
      areasOfDevelopment: [],
      externalRoles: [],
    };

    if (empDetails?.data) {
      const empData = empDetails.data;

      processedData.time_in_current_role = empData.time_in_current_role || "";
      processedData.education_degree = empData.education_degree || "";
      processedData.certificates = empData.certificates || "";
      processedData.willingness_to_relocate =
        empData.willingness_to_relocate || "";
      processedData.next_role_preference = empData.next_role_preference || "";
      processedData.timing_for_next_role = empData.timing_for_next_role || "";
      processedData.last_year_top_achievement =
        empData.last_year_top_achievement || "";
      processedData.last_year_personal_development_review =
        empData.last_year_personal_development_review || "";
      processedData.goal_next_two_quarters =
        empData.goal_next_two_quarters || "";

      if (empData.image) {
        processedData.image = empData.image;
      }

      processedData.emp_languages = empData.emp_languages || [];
      processedData.EmpLocationPreferences =
        empData.EmpLocationPreferences || [];
      processedData.work_experiences = empData.work_experiences || [];
      processedData.EmpResourceGroup = empData.EmpResourceGroup || [];
      processedData.EmpTtecCommunities = empData.EmpTtecCommunities || [];
      processedData.EmpLeadershipCulture = empData.EmpLeadershipCulture || [];

      processedData.accomplishments = empData.last_year_top_achievement || "";
      processedData.redo = empData.last_year_personal_development_review || "";
      processedData.goals = empData.goal_next_two_quarters || "";

      if (
        Array.isArray(empData.EmpTtecCommunities) &&
        empData.EmpTtecCommunities.length > 0
      ) {
        processedData.ttecCommunity =
          empData.EmpTtecCommunities[0].ttec_communities || "";
      }

      if (Array.isArray(empData.work_experiences)) {
        processedData.ttecRoles = empData.work_experiences
          .filter((w: any) => w.company_name === "TTEC")
          .map((w: any) => ({
            emp_work_experience_uid: w.emp_work_experience_uid,
            previousRole: w.previous_position_role,
            previousStartingDate: w.starting_date,
            previousEndingDate: w.end_date,
            currentRole: w.current_position || false,
          }));

        processedData.externalRoles = empData.work_experiences
          .filter((w: any) => w.company_name !== "TTEC")
          .map((w: any) => ({
            emp_work_experience_uid: w.emp_work_experience_uid,
            roleTitle: w.previous_position_role,
            company: w.company_name,
            roleStartingDate: w.starting_date,
            roleEndingDate: w.end_date,
          }));
      }

      if (Array.isArray(empData.EmpLeadershipCulture)) {
        processedData.strengthsDisplayed = empData.EmpLeadershipCulture.filter(
          (lc: any) => lc.flag === "STRENGTH",
        )
          .map(
            (lc: any) =>
              lc.leadership_culture_dropdown_sd
                ?.leadership_culture_dropdown_uid,
          )
          .filter(Boolean);

        processedData.areasOfDevelopment = empData.EmpLeadershipCulture.filter(
          (lc: any) => lc.flag === "DEVELOPMENT",
        )
          .map(
            (lc: any) =>
              lc.leadership_culture_dropdown_sd
                ?.leadership_culture_dropdown_uid,
          )
          .filter(Boolean);
      }

      if (Array.isArray(empData.emp_languages)) {
        processedData.languages = empData.emp_languages
          .map((l: any) => l.language_dropdown_uid)
          .filter(Boolean);
      }

      if (Array.isArray(empData.EmpLocationPreferences)) {
        processedData.locationPreferences = empData.EmpLocationPreferences.map(
          (loc: any) => loc.location_dropdown_uid,
        ).filter(Boolean);
      }
    }

    if (empOracleDetails?.data?.response?.message) {
      const oracleMsg = empOracleDetails.data.response.message;

      processedData.oracle_id =
        oracleMsg.Employee_Oracle || processedData.oracle_id;
      processedData.name =
        `${oracleMsg.Employee_FirstName || ""} ${oracleMsg.Employee_LastName || ""}`.trim() ||
        processedData.name;
      processedData.manager_oracle = oracleMsg.Manager_Oracle || "";
      processedData.manager_email = oracleMsg.Manager_Email || "";
      processedData.country = oracleMsg.Country || "";
      processedData.location_desc = oracleMsg.Location_Desc || "";
      processedData.employee_status = oracleMsg.Employee_Status || "";
      processedData.jobLevel = oracleMsg.Employee_Level || "";

      if (oracleMsg.Job_Code) {
        processedData.jobTitle = oracleMsg.Job_Code.includes(".")
          ? oracleMsg.Job_Code.split(".")[1].trim()
          : oracleMsg.Job_Code;
      }

      if (oracleMsg.Employee_Image) {
        processedData.image = oracleMsg.Employee_Image;
      }

      if (oracleMsg.Hire_Date) {
        const hireDate = new Date(oracleMsg.Hire_Date);
        if (!isNaN(hireDate.getTime())) {
          const currentDate = new Date();
          const yearDiff = currentDate.getFullYear() - hireDate.getFullYear();
          const monthDiff = currentDate.getMonth() - hireDate.getMonth();

          if (monthDiff < 0) {
            processedData.timeAtTTEC = `${yearDiff - 1} Year${yearDiff - 1 !== 1 ? "s" : ""} ${12 + monthDiff} Month${12 + monthDiff !== 1 ? "s" : ""}`;
          } else {
            processedData.timeAtTTEC = `${yearDiff} Year${yearDiff !== 1 ? "s" : ""} ${monthDiff} Month${monthDiff !== 1 ? "s" : ""}`;
          }
        }
      }
    }

    return processedData;
  };

  const generateBulkPDFBackground = async (
    employeeDataArray: any[],
    isExport: boolean = true,
  ): Promise<{ blob: Blob; fileName: string } | null> => {
    if (!employeeDataArray || employeeDataArray.length === 0) {
      return null;
    }

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "in",
      format: [14.65, 12.32],
      compress: true,
    });

    setExportProgress((prev) => ({
      ...prev,
      phase: "Generating PDF pages...",
    }));

    try {
      for (let i = 0; i < employeeDataArray.length; i++) {
        const employeeData = employeeDataArray[i];

        setExportProgress((prev) => ({
          current: i + 1,
          total: employeeDataArray.length,
          phase: `Generating page ${i + 1} of ${employeeDataArray.length}...`,
        }));

        if (i > 0) {
          pdf.addPage();
        }

        const offscreenDiv = document.createElement("div");
        offscreenDiv.style.position = "absolute";
        offscreenDiv.style.left = "-9999px";
        offscreenDiv.style.top = "-9999px";
        offscreenDiv.style.width = "1700px";
        offscreenDiv.style.fontSize = "13px";
        offscreenDiv.style.lineHeight = "1.2";
        offscreenDiv.style.color = "#000";
        offscreenDiv.style.backgroundColor = "#fff";
        offscreenDiv.style.padding = "50px 100px 50px 100px";
        offscreenDiv.style.visibility = "visible";

        document.body.appendChild(offscreenDiv);
        const root = createRoot(offscreenDiv);

        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(
              new Error(`Timeout rendering employee: ${employeeData.name}`),
            );
          }, 10000);

          root.render(
            React.createElement(CareerCardHtml, { data: employeeData }),
          );

          requestAnimationFrame(() => {
            setTimeout(() => {
              clearTimeout(timeout);
              resolve();
            }, 200);
          });
        });

        const canvas = await html2canvas(offscreenDiv, {
          scale: 1.2,
          useCORS: true,
          backgroundColor: "#fff",
          logging: false,
          allowTaint: false,
          removeContainer: false,
          height: offscreenDiv.scrollHeight,
          width: offscreenDiv.scrollWidth,
          imageTimeout: 5000,
          onclone: (clonedDoc: any) => {
            const clonedElement = clonedDoc.body.querySelector("div");
            if (clonedElement) {
              clonedElement.style.visibility = "visible";
              clonedElement.style.display = "block";
            }
          },
        });

        if (canvas.width === 0 || canvas.height === 0) {
          root.unmount();
          document.body.removeChild(offscreenDiv);
          continue;
        }

        const imgData = canvas.toDataURL("image/jpeg", 0.85);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgProps = pdf.getImageProperties(imgData);
        const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

        if (imgHeight > pdfHeight) {
          const scaledHeight = pdfHeight;
          const scaledWidth = (imgProps.width * scaledHeight) / imgProps.height;
          pdf.addImage(
            imgData,
            "JPEG",
            (pdfWidth - scaledWidth) / 2,
            0,
            scaledWidth,
            scaledHeight,
          );
        } else {
          pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, imgHeight);
        }

        root.unmount();
        document.body.removeChild(offscreenDiv);
        canvas.remove();

        if (window.gc) {
          window.gc();
        }

        if (i < employeeDataArray.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }

      const fileName = `Team_Career_Cards_${new Date().toISOString().split("T")[0].replace(/-/g, "")}.pdf`;
      const blob = pdf.output("blob");

      if (isExport) {
        pdf.save(fileName);
      }

      return { blob, fileName };
    } catch (error) {
      throw error;
    }
  };

  const handleExportToPDF = async () => {
    if (selectedReportees.size === 0) {
      setShowExportValidation(true);
      setTimeout(() => setShowExportValidation(false), 5000);
      return;
    }

    setIsExporting(true);
    setShowProgressIndicator(true);
    const selectedData = reportees.filter((rep) =>
      selectedReportees.has(rep.workEmail),
    );
    setExportProgress({
      current: 0,
      total: selectedData.length,
      phase: "Starting export...",
    });

    try {
      showToast(
        `Starting export for ${selectedData.length} employees...`,
        "success",
      );

      const BATCH_SIZE = 6;
      const API_DELAY = 50;
      const batches = [];
      for (let i = 0; i < selectedData.length; i += BATCH_SIZE) {
        batches.push(selectedData.slice(i, i + BATCH_SIZE));
      }

      const allEmployeeData: any[] = [];
      processedCountRef.current = 0;
      apiFailureCountRef.current = 0;

      setExportProgress((prev) => ({
        ...prev,
        phase: "Fetching employee data...",
      }));

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];

        const batchPromises = batch.map(async (reportee) => {
          let empDetailsData = null;
          let empOracleData = null;

          try {
            const empDetailsResult = await dispatch(
              fetchEmpDetails(reportee.oracleId),
            );

            if (
              empDetailsResult.type === "empDetails/fetchEmpDetails/fulfilled"
            ) {
              empDetailsData = empDetailsResult.payload;
            } else if (
              empDetailsResult.type === "empDetails/fetchEmpDetails/rejected"
            ) {
              empDetailsData = createEmptyEmpDetailsData();
              apiFailureCountRef.current++;
            }
          } catch (error) {
            empDetailsData = createEmptyEmpDetailsData();
            apiFailureCountRef.current++;
          }

          try {
            const empOracleResult = await dispatch(
              fetchEmpOracleDetails(reportee.oracleId),
            );

            if (
              empOracleResult.type ===
              "empOracleDetails/fetchEmpOracleDetails/fulfilled"
            ) {
              empOracleData = empOracleResult.payload;
            } else if (
              empOracleResult.type ===
              "empOracleDetails/fetchEmpOracleDetails/rejected"
            ) {
              empOracleData = createEmptyOracleDetailsData();
              apiFailureCountRef.current++;
            }
          } catch (error) {
            empOracleData = createEmptyOracleDetailsData();
            apiFailureCountRef.current++;
          }

          const processedData = processEmployeeData(
            { data: empDetailsData },
            empOracleData,
            reportee,
          );

          processedCountRef.current++;
          setExportProgress((prev) => ({
            ...prev,
            current: processedCountRef.current,
            phase: `Processing employee ${processedCountRef.current} of ${selectedData.length}...`,
          }));

          return { data: processedData, error: null };
        });

        const batchResults = await Promise.all(batchPromises);
        allEmployeeData.push(...batchResults);

        if (batchIndex < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, API_DELAY));
        }
      }

      const successfulEmployees = allEmployeeData.map((result) => result.data);

      if (apiFailureCountRef.current > 0) {
        showToast(
          `Warning: ${apiFailureCountRef.current} employee/employees has basic information only.`,
          "warning",
        );
      }

      const pdfResult = await generateBulkPDFBackground(
        successfulEmployees,
        true,
      );

      if (pdfResult) {
        let message = `Successfully exported ${successfulEmployees.length} employee records to PDF!`;
        if (apiFailureCountRef.current > 0) {
          message += ` (${apiFailureCountRef.current} employee/employees had limited data)`;
        }
        showToast(message, "success");
      } else {
        showToast("PDF generation failed. Please try again.", "error");
      }
    } catch (error) {
      showToast(
        "An error occurred while exporting to PDF. Please try again.",
        "error",
      );
    } finally {
      setIsExporting(false);
      setShowProgressIndicator(true);
      setExportProgress({ current: 0, total: 0, phase: "idle" });
    }
  };

  const createEmptyEmpDetailsData = (): EmpDetails => {
    return {
      emp_details_uid: 0,
      oracle_id: "",
      email_id: "",
      image: "",
      year: new Date().getFullYear().toString(),
      half_year: "H1",
      current_location: "Not Available",
      willingness_to_relocate: "Not Specified",
      next_role_preference: "Not Specified",
      timing_for_next_role: "Not Specified",
      last_year_top_achievement: "Not Specified",
      last_year_personal_development_review: "Not Specified",
      goal_next_two_quarters: "Not Specified",
      certificates: "Not Available",
      education_degree: "Not Available",
      future_career_aspirations: "Not Available",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      work_experiences: [],
      emp_languages: [],
      EmpLocationPreferences: [],
      EmpResourceGroup: [],
      EmpTtecCommunities: [],
      EmpLeadershipCulture: [],
    };
  };

  const createEmptyOracleDetailsData = () => {
    return {
      response: {
        message: {
          Employee_Oracle: "",
          Employee_FirstName: "",
          Employee_LastName: "",
          Manager_Oracle: "",
          Manager_Email: "",
          Country: "Not Available",
          Location_Desc: "Not Available",
          Employee_Status: "Active",
          Employee_Level: "Not Available",
          Job_Code: "Not Available",
          Employee_Image: null,
          Hire_Date: null,
        },
      },
    };
  };

  const ProgressIndicator = () => {
    if (!isExporting || !showProgressIndicator) return null;

    const progressPercentage =
      exportProgress.total > 0
        ? Math.round((exportProgress.current / exportProgress.total) * 100)
        : 0;

    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          color: "#333333",
          padding: "30px 40px",
          borderRadius: "12px",
          border: "2px solid rgb(244, 247, 249)",
          boxShadow: "0 15px 35px rgba(0, 0, 0, 0.3)",
          zIndex: 9999,
          textAlign: "center",
          fontSize: "18px",
          fontWeight: "600",
          minWidth: "320px",
          maxWidth: "450px",
        }}
      >
        <button
          onClick={() => {
            setShowProgressIndicator(false);
            showToast(
              "You can close this dialog and pdf generation continues in background.",
              "success",
            );
          }}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            background: "transparent",
            border: "none",
            fontSize: "24px",
            color: "#666",
            cursor: "pointer",
            lineHeight: "1",
            padding: "0",
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f0f0f0";
            e.currentTarget.style.color = "#333";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "#666";
          }}
          title="Close progress indicator (process continues in background)"
          aria-label="Close progress indicator"
        >
          <i className="bi bi-x" aria-hidden="true"></i>
        </button>

        <div
          style={{
            fontSize: "16px",
            fontWeight: "600",
            marginBottom: "8px",
            color: "#333",
            paddingRight: "30px",
          }}
        >
          Processing Team Career Cards
        </div>

        <div
          style={{
            fontSize: "14px",
            color: "#666",
            marginBottom: "16px",
          }}
        >
          {exportProgress.phase}
        </div>

        <div
          style={{
            fontSize: "12px",
            color: "#888",
            marginBottom: "12px",
          }}
        >
          {exportProgress.current} of {exportProgress.total} (
          {progressPercentage}%)
        </div>

        <div
          style={{
            width: "100%",
            height: "8px",
            backgroundColor: "#f0f0f0",
            borderRadius: "4px",
            overflow: "hidden",
            marginBottom: "12px",
          }}
        >
          <div
            style={{
              width: `${progressPercentage}%`,
              height: "100%",
              backgroundColor: "#28a745",
              borderRadius: "4px",
              transition: "width 0.3s ease",
            }}
          ></div>
        </div>

        <div
          style={{
            fontSize: "11px",
            color: "#999",
            fontStyle: "italic",
            lineHeight: "1.3",
          }}
        >
          You can close this dialog. The process will continue in the
          background.
        </div>
      </div>
    );
  };

  const handleViewDetails = (empId: string) => {
    try {
      dispatch(resetEmpDetails());
      dispatch(resetEmpOracleDetails());

      // Validate email format
      // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!empId) {
        throw new Error("Invalid EmployeeId");
      }

      setSelectedEmployeeId(empId);
      setModalLoading(true);
      setModalError(null);
      setShowCareerCardModal(true);

      // Optional: Add any pre-loading logic here
      // For example, you could pre-validate if employee exists

      // Simulate brief loading for better UX
      setTimeout(() => {
        setModalLoading(false);
      }, 500);
    } catch (error) {
      console.error("Error opening career details:", error);
      setModalError(
        error instanceof Error
          ? error.message
          : "Failed to load career details",
      );
      setModalLoading(false);
      setShowCareerCardModal(true);
    }
  };

  const handleCloseModal = useCallback((): void => {
    dispatch(resetEmpDetails());
    dispatch(resetEmpOracleDetails());

    setShowCareerCardModal(false);
    setSelectedEmployeeId("");
    setModalLoading(false);
    setModalError(null);
  }, [dispatch]);

  const handleRetry = useCallback((): void => {
    if (selectedEmployeeId) {
      handleViewDetails(selectedEmployeeId);
    }
  }, [selectedEmployeeId, handleViewDetails]);

  const handleCheckboxChange = (mail: string) => {
    const newSelected = new Set(selectedReportees);
    if (newSelected.has(mail)) {
      newSelected.delete(mail);
    } else {
      newSelected.add(mail);
    }
    setSelectedReportees(newSelected);
    setSelectAll(newSelected.size === reportees.length);
    setShowExportValidation(false);
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedReportees(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(reportees.map((rep) => rep.workEmail));
      setSelectedReportees(allIds);
      setSelectAll(true);
    }
    setShowExportValidation(false);
  };

  const getManagerName = () => {
    if (hierarchyDetails.status === "succeeded" && hierarchyDetails.data) {
      const data: any = hierarchyDetails.data;

      try {
        if (
          data.data?.response?.message?.data &&
          Array.isArray(data.data.response.message.data) &&
          data.data.response.message.data.length > 0
        ) {
          return data.data.response.message.data[0].manager_name || "Manager";
        }

        if (
          data.data?.response?.message?.result?.returnvals?.["0"]?.value &&
          Array.isArray(
            data.data.response.message.result.returnvals["0"].value,
          ) &&
          data.data.response.message.result.returnvals["0"].value.length > 0
        ) {
          return (
            data.data.response.message.result.returnvals["0"].value[0]
              .manager_name || "Manager"
          );
        }

        if (data.data && Array.isArray(data.data) && data.data.length > 0) {
          return data.data[0].manager_name || "Manager";
        }

        if (
          data.response?.message?.data &&
          Array.isArray(data.response.message.data) &&
          data.response.message.data.length > 0
        ) {
          return data.response.message.data[0].manager_name || "Manager";
        }

        if (
          data.response?.message?.result?.returnvals?.["0"]?.value &&
          Array.isArray(data.response.message.result.returnvals["0"].value) &&
          data.response.message.result.returnvals["0"].value.length > 0
        ) {
          return (
            data.response.message.result.returnvals["0"].value[0]
              .manager_name || "Manager"
          );
        }

        if (data.response?.message?.Manager_Oracle) {
          return data.response.message.Manager_Oracle || "Manager";
        }
      } catch (err) {
        return "Manager";
      }
    }
    return "Manager";
  };

  useEffect(() => {
    setSelectedReportees(new Set());
    setSelectAll(false);
    setShowExportValidation(false);
  }, [reportees]);

  // Updated initial fetch with progress tracking
  useEffect(() => {
    const fetchReporteesData = async () => {
      setIsInitialLoading(true);
      setInitialLoadingProgress({
        current: 0,
        total: 1,
        status: "Fetching team hierarchy...",
      });

      try {
        await dispatch(fetchEmpOracleHierarchyDetails("NA"));
      } catch (err) {
        setError("Failed to fetch reportees data");
        setIsInitialLoading(false);
        setLoading(false);
      }
    };

    fetchReporteesData();
  }, [dispatch, currentManagerOracleId]);

  useEffect(() => {
    if (hierarchyDetails.status === "succeeded" && hierarchyDetails.data) {
      try {
        let reporteesData: EmployeeData[] = [];
        const data: any = hierarchyDetails.data;

        if (
          data.data?.response?.message?.data &&
          Array.isArray(data.data.response.message.data)
        ) {
          reporteesData = data.data.response.message.data;
        } else if (
          data.data?.response?.message?.result?.returnvals?.["0"]?.value &&
          Array.isArray(data.data.response.message.result.returnvals["0"].value)
        ) {
          reporteesData =
            data.data.response.message.result.returnvals["0"].value;
        } else if (data.data && Array.isArray(data.data)) {
          reporteesData = data.data;
        } else if (
          data.response?.message?.data &&
          Array.isArray(data.response.message.data)
        ) {
          reporteesData = data.response.message.data;
        } else if (
          data.response?.message?.result?.returnvals?.["0"]?.value &&
          Array.isArray(data.response.message.result.returnvals["0"].value)
        ) {
          reporteesData = data.response.message.result.returnvals["0"].value;
        } else if (data.response?.message?.result?.returnvals) {
          const message = data.response.message;
          const firstName = message.Employee_FirstName || "";
          const lastName = message.Employee_LastName || "";
          const fullName = `${firstName} ${lastName}`.trim();
          const hireDate = message.Hire_Date
            ? new Date(message.Hire_Date)
            : null;

          const currentDate = new Date();
          let timeAtCompany = "N/A";

          if (hireDate) {
            const yearDiff = currentDate.getFullYear() - hireDate.getFullYear();
            const monthDiff = currentDate.getMonth() - hireDate.getMonth();

            if (yearDiff > 0) {
              timeAtCompany =
                monthDiff < 0
                  ? `${yearDiff - 1} Year${yearDiff - 1 !== 1 ? "s" : ""} ${12 + monthDiff} Month${12 + monthDiff !== 1 ? "s" : ""}`
                  : `${yearDiff} Year${yearDiff !== 1 ? "s" : ""} ${monthDiff} Month${monthDiff !== 1 ? "s" : ""}`;
            } else {
              timeAtCompany = `${monthDiff} Month${monthDiff !== 1 ? "s" : ""}`;
            }
          }

          const singleReportee: Reportee[] = [
            {
              name: fullName,
              oracleId: message.Employee_Oracle || "",
              jobLevel: message.Employee_Level || "N/A",
              timeAtTTEC: timeAtCompany,
              workEmail: message.Manager_Email || "",
              imageUrl: DEFAULT_IMAGE,
            },
          ];

          setReportees(singleReportee);
          return;
        } else {
          setReportees([]);
          return;
        }

        if (reporteesData.length > 0) {
          const mappedReportees = reporteesData.map((emp: EmployeeData) => {
            const nameParts = emp.employee_name
              ? emp.employee_name.split(",")
              : ["", ""];
            const lastName = nameParts[0] ? nameParts[0].trim() : "";
            const firstNames = nameParts.length > 1 ? nameParts[1].trim() : "";
            const formattedName = `${firstNames} ${lastName}`.trim();

            const hireDate = emp.hire_date ? new Date(emp.hire_date) : null;
            const currentDate = new Date();
            let timeAtCompany = "N/A";

            if (hireDate) {
              const yearDiff =
                currentDate.getFullYear() - hireDate.getFullYear();
              const monthDiff = currentDate.getMonth() - hireDate.getMonth();

              if (yearDiff > 0) {
                timeAtCompany =
                  monthDiff < 0
                    ? `${yearDiff - 1} Year${yearDiff - 1 !== 1 ? "s" : ""} ${12 + monthDiff} Month${12 + monthDiff !== 1 ? "s" : ""}`
                    : `${yearDiff} Year${yearDiff !== 1 ? "s" : ""} ${monthDiff} Month${monthDiff !== 1 ? "s" : ""}`;
              } else {
                timeAtCompany = `${monthDiff} Month${monthDiff !== 1 ? "s" : ""}`;
              }
            }

            return {
              name: formattedName,
              oracleId: emp.employee_id || "",
              jobLevel: emp.job_level || "N/A",
              timeAtTTEC: timeAtCompany,
              workEmail: emp.work_email || "N/A",
              imageUrl: DEFAULT_IMAGE,
            };
          });

          setReportees(mappedReportees);
        } else {
          setReportees([]);
        }
      } catch (err) {
        setError("Error processing reportees data");
      }
    } else if (hierarchyDetails.status === "failed") {
      setError(hierarchyDetails.error || "Failed to fetch reportees");
    }
  }, [hierarchyDetails.status, hierarchyDetails.data, hierarchyDetails.error]);

  return (
    <div>
      {/* Show initial loading progress */}
      {isInitialLoading && <InitialLoadingProgress />}

      {/* Export progress indicator */}
      <ProgressIndicator />

      {/* Toast notifications */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <div className="header">
        <div className="d-flex justify-content-between align-items-center px-2 rounded-3">
          <div>
            <h5 className="fw-bold mb-1 p-2 rounded-2">Team Members</h5>
            {!loading && !error && reportees.length > 0 && (
              <div className="ms-2">
                <small>
                  Showing {reportees.length} direct reportees for{" "}
                  {getManagerName()}
                </small>
              </div>
            )}
          </div>
        </div>
      </div>

      {!loading && !error && reportees.length > 0 && (
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center p-3 bg-light border rounded mx-2 mt-3">
          <div className="d-flex align-items-center mb-2 mb-md-0">
            <div
              className="form-check me-3"
              style={{ display: "flex", alignItems: "center" }}
            >
              <input
                className="form-check-input"
                type="checkbox"
                id="selectAll"
                checked={selectAll}
                onChange={handleSelectAllChange}
                style={{ margin: 0, marginRight: "0.5rem" }}
              />
              <label
                className="form-check-label fw-semibold"
                htmlFor="selectAll"
                style={{ marginBottom: 0, lineHeight: "1.5" }}
              >
                Select All ({reportees.length})
              </label>
            </div>
            <span className="text-muted small" style={{ lineHeight: "1.5" }}>
              {selectedReportees.size} of {reportees.length} selected
            </span>
          </div>
          <div className="d-flex align-items-center">
            {isExporting ? (
              <button
                className="btn btn-success btn-sm d-flex align-items-center"
                disabled
              >
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                Processing... ({exportProgress.current}/{exportProgress.total})
              </button>
            ) : (
              <button
                className="btn btn-success btn-sm d-flex align-items-center"
                onClick={handleExportToPDF}
                disabled={loading}
              >
                <i className="bi bi-file-earmark-pdf me-2"></i>
                Export to PDF
              </button>
            )}
          </div>
        </div>
      )}

      {showExportValidation && (
        <div
          className="alert alert-warning mx-2 mt-2 d-flex align-items-center"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <div>
            <strong>Selection Required:</strong> Please select at least one
            record to export.
          </div>
          <button
            type="button"
            className="btn-close ms-auto"
            onClick={() => setShowExportValidation(false)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger m-4" role="alert">
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            <div>
              <h5 className="alert-heading mb-1">Error Loading Data</h5>
              <p className="mb-0">{error}</p>
            </div>
          </div>
          <div className="mt-3">
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => {
                setIsInitialLoading(true);
                setInitialLoadingProgress({
                  current: 0,
                  total: 1,
                  status: "Retrying...",
                });
                setError(null);
                dispatch(fetchEmpOracleHierarchyDetails("NA"));
              }}
            >
              <i className="bi bi-arrow-clockwise me-1"></i> Retry
            </button>
          </div>
        </div>
      )}

      {!loading && !error && reportees.length === 0 && !isInitialLoading && (
        <div className="alert alert-info m-4" role="alert">
          <div className="d-flex align-items-center">
            <i className="bi bi-info-circle-fill me-2"></i>
            <div>
              <h5 className="alert-heading mb-1">No Reportees Found</h5>
              <p className="mb-0">
                There are no direct reportees for this manager in the system.
              </p>
            </div>
          </div>
        </div>
      )}

      <div style={{ overflowX: "hidden", overflowY: "auto", width: "100%" }}>
        <div className="row mt-3 g-0">
          {reportees.map((rep, idx) => (
            <div className="col-sm-6 col-md-4 col-lg-3 mb-4" key={idx}>
              <div
                className="card h-100 border border-dark p-0 m-1"
                style={{
                  background: "#f5f6fa",
                  boxShadow: "0 4px 16px 0 rgba(0, 123, 255, 0.15)",
                }}
              >
                <div
                  className="position-absolute"
                  style={{ top: "10px", left: "10px", zIndex: 10 }}
                >
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`checkbox-${rep.workEmail}`}
                      checked={selectedReportees.has(rep.workEmail)}
                      onChange={() => handleCheckboxChange(rep.workEmail)}
                      style={{
                        backgroundColor: selectedReportees.has(rep.workEmail)
                          ? "#007bff"
                          : "#fff",
                        borderColor: "#007bff",
                        transform: "scale(1.2)",
                      }}
                    />
                  </div>
                </div>

                <div className="d-flex flex-column h-100">
                  <div
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{
                      flex: 1,
                      borderBottom: "1px solid #000",
                      background: "rgb(26 151 134)",
                    }}
                  >
                    <img
                      src={rep.imageUrl}
                      alt={rep.name}
                      className="rounded-circle mb-2 mt-3"
                      style={{
                        width: 70,
                        height: 70,
                        objectFit: "cover",
                        border: "2px solid #b6e0fe",
                      }}
                    />
                  </div>

                  <div
                    className="d-flex flex-column justify-content-center p-3"
                    style={{ flex: 1, alignItems: "flex-start" }}
                  >
                    <h5
                      className="fw-bold mb-1"
                      style={{
                        fontSize: 22,
                        color: "#222",
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      {rep.name}
                    </h5>
                    <div
                      className="mb-1"
                      style={{
                        fontSize: 15,
                        color: "#555",
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      <span className="fw-semibold">Oracle ID:</span>{" "}
                      {rep.oracleId}
                    </div>
                    <div
                      className="mb-1"
                      style={{
                        fontSize: 15,
                        color: "#555",
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      <span className="fw-semibold">Job Level:</span>{" "}
                      {rep.jobLevel}
                    </div>
                    <div
                      style={{
                        fontSize: 15,
                        color: "#555",
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      <span className="fw-semibold">Time at TTEC:</span>{" "}
                      {rep.timeAtTTEC}
                    </div>
                    <div className="d-flex justify-content-end w-100 mt-3">
                      <button
                        className="btn btn-primary btn-sm px-3 shadow"
                        style={{
                          boxShadow: "0 2px 8px 0 rgba(0, 123, 255, 0.25)",
                        }}
                        onClick={() => handleViewDetails(rep.oracleId)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog
        header={
          <div style={{ display: "flex", alignItems: "center" }}>
            <i className="pi pi-user" style={{ marginRight: "0.5rem" }}></i>
            Employee Career Details
            {selectedEmployeeId && (
              <span
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "normal",
                  marginLeft: "0.5rem",
                }}
              >
                ({selectedEmployeeId})
              </span>
            )}
          </div>
        }
        visible={showCareerCardModal}
        onHide={handleCloseModal}
        style={{ width: "95vw", maxWidth: "1400px", minHeight: "700px" }}
        maximizable
        modal
        draggable={false}
        resizable={false}
        className="career-card-modal"
        headerStyle={{
          padding: "1rem 1.5rem",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: "8px 8px 0 0",
        }}
        contentStyle={{
          padding: modalLoading || modalError ? "2rem" : "0",
          background: "#f8fafc",
          borderRadius: "0 0 8px 8px",
        }}
        footer={
          modalError ? (
            <div style={{ textAlign: "right" }}>
              <Button
                label="Close"
                onClick={handleCloseModal}
                className="p-button-text"
                icon="pi pi-times"
              />
            </div>
          ) : null
        }
      >
        {modalLoading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              fontSize: "1.125rem",
              color: "#6b7280",
              flexDirection: "column",
            }}
          >
            <ProgressSpinner style={{ width: "50px", height: "50px" }} />
            <span style={{ marginTop: "1rem" }}>Loading career details...</span>
          </div>
        )}

        {modalError && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              padding: "2rem",
              textAlign: "center",
            }}
          >
            <i
              className="pi pi-exclamation-triangle"
              style={{
                fontSize: "3rem",
                color: "#dc2626",
                marginBottom: "1rem",
              }}
            ></i>
            <div
              style={{
                color: "#dc2626",
                fontSize: "1.125rem",
                marginBottom: "1rem",
              }}
            >
              {modalError}
            </div>
            <Button
              label="Try Again"
              onClick={handleRetry}
              className="p-button-primary"
              icon="pi pi-refresh"
            />
          </div>
        )}

        {!modalLoading && !modalError && selectedEmployeeId && (
          <CareerCard
            empId={selectedEmployeeId}
            isView={true}
            username={props.username}
          />
        )}
      </Dialog>
    </div>
  );
};

export default ReporteeDetails;

// TODO: Add the Career Card Modal to the JSX return statement before the closing </div>
// The modal should be inserted in the JSX return statement inside the main div
// Here's the modal JSX code that needs to be added:
